// Formatting for the VPN's screens.
import type { VpnPolicy, VpnSession } from './api';

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

/** 1,536 → "1.5 KB": powers of 1024, one decimal above bytes. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return unit === 0 ? `${String(value)} B` : `${value.toFixed(1)} ${UNITS[unit]}`;
}

const PROTOCOLS: Record<number, string> = { 1: 'ICMP', 6: 'TCP', 17: 'UDP', 58: 'ICMPv6' };

/** The IP protocol's name, or its number. */
export function protocolName(protocol: number | null | undefined): string {
  if (protocol == null) return '—';
  return PROTOCOLS[protocol] ?? String(protocol);
}

// The backend's end reasons (website-backend services/vpn/sessions.rs).
const END_REASONS: Record<string, string> = {
  // Also a newer session from the same device at the same gateway.
  user: 'Ended by the user',
  // Also the VPN turned off at the gateway.
  ended_by_admin: 'Ended by someone else',
  expired: 'Expired',
  device_revoked: 'Device revoked',
  gateway_reset: 'The gateway reset its VPN',
  policy_withdrawn: 'No policy allows it any more',
};

const PATHS: Record<string, string> = {
  no_preference: 'No preference (direct, for now)',
  direct_preferred: 'Direct preferred',
  direct_only: 'Direct only',
  relay_preferred: 'Relay preferred',
  relay_only: 'Relay only',
};

export function pathSettingLabel(value: string): string {
  return PATHS[value] ?? value;
}

/** 480 → "8 hours", 90 → "1 hour 30 minutes". */
/** Where a policy applies, in a few words. */
export function placesLabel(policy: Pick<VpnPolicy, 'wholeOrganization' | 'places'>): string {
  if (policy.wholeOrganization) return 'Every site';
  if (policy.places.length === 0) return 'Nowhere';
  return policy.places.map((p) => (p.kind === 'division' ? `Every site in ${p.name}` : p.name)).join(', ');
}

export function minutesLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hours = h === 0 ? '' : `${String(h)} hour${h === 1 ? '' : 's'}`;
  const mins = m === 0 ? '' : `${String(m)} minute${m === 1 ? '' : 's'}`;
  return [hours, mins].filter(Boolean).join(' ') || '0 minutes';
}

/** A session's end reason in words; one this build doesn't know stays as sent. */
export function endReasonLabel(reason: string | null): string {
  if (!reason) return 'Ended';
  return END_REASONS[reason] ?? reason;
}

/** "a, b" or one per line, the way a field holds a list → the list itself. */
export function parseList(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

export type SessionState = 'live' | 'connecting' | 'ended';

/** Live once the client has shaken hands, connecting before; ended when it has an end. */
export function sessionState(session: VpnSession): SessionState {
  if (session.endedAt) return 'ended';
  return session.startedAt ? 'live' : 'connecting';
}

export function userLabel(session: { userName: string | null; userEmail: string | null; userId: string }): string {
  return session.userName ?? session.userEmail ?? session.userId;
}

const PLATFORMS: Record<string, string> = {
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
  android: 'Android',
  ios: 'iOS',
  chromeos: 'ChromeOS',
};

/** An app's platform, or a browser's operating system, by its name. */
export function platformLabel(platform: string | null): string {
  if (!platform) return 'an unknown system';
  return PLATFORMS[platform] ?? platform;
}

/** How long a session has left, from `now`. */
export function timeLeftLabel(expiresAt: string, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.round((new Date(expiresAt).getTime() - now) / 60_000));
  return minutes === 0 ? 'under a minute' : minutesLabel(minutes);
}

/** The platforms this browser may run on, as the downloads name them; none
 *  for one the app isn't built for. Only a distribution's own build of a
 *  browser names the distribution (Ubuntu's Firefox says "Ubuntu"), and Chrome
 *  never does, so a browser that says just "Linux" may want either Linux
 *  package. */
export function browserPlatforms(userAgent: string = navigator.userAgent): string[] {
  if (/Android|CrOS|iPhone|iPad/i.test(userAgent)) return [];
  if (/Windows/i.test(userAgent)) return ['windows-x86_64'];
  if (/Macintosh|Mac OS X/i.test(userAgent)) return ['macos-universal'];
  if (/Fedora|Red Hat|CentOS|Rocky|AlmaLinux/i.test(userAgent)) return ['rpm-x86_64'];
  if (/Ubuntu|Debian/i.test(userAgent)) return ['linux-amd64'];
  if (/Linux|X11/i.test(userAgent)) return ['linux-amd64', 'rpm-x86_64'];
  return [];
}

/** The Mac package's install command: `curl` sets no quarantine flag, so
 *  Gatekeeper never checks the unsigned package (docs/VpnAccess.md, "Slice 16
 *  design"). */
export function macInstallCommand(url: string): string {
  return `curl -fsSL -o /tmp/Witina-VPN.pkg '${url}' && sudo installer -pkg /tmp/Witina-VPN.pkg -target /`;
}

/** The Mac package's way to remove everything it installed. */
export const MAC_UNINSTALL_COMMAND = 'sudo "/Library/Application Support/Witina VPN/uninstall"';

/** "192.168.1.0/24 is 100.96.1.0/24", or why a range has no alias. */
export function aliasLabel(alias: { real: string; alias: string | null }): string {
  return alias.alias ? `${alias.real} is ${alias.alias}` : `${alias.real} has no alias (none free on this computer)`;
}
